import React, { useEffect, useState } from "react";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  Button,
  Header,
  Pagination,
  Table,
  Box,
  PropertyFilter,
  PropertyFilterProps,
  TableProps,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import {
  FixedFeeConfig,
  FixedFeeType,
  FuseAccountingCentralDatastore,
  GetFixedFeeConfigCommand,
} from "@amzn/fuse-accounting-central-datastore-client";
import { NumericValue } from "@smithy/core/serde";
import {
  FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS,
  FIXED_FEE_CONFIG_ATTRIBUTES,
  FIXED_FEE_CONFIG_FILTERS,
  FUSE_OFFER_LINKS_CELL_WRAPPER,
  getFixedFeeConfigTableColDefs,
  LINK_CELL_WRAPPER,
  NUMBER_CELL_WRAPPER,
  STRING_OR_DATE_CELL_WRAPPER,
} from "src/components/allFixedFeeConfigsPage/fixed-fee-config-attributes";
import AdditionalInfoPopover, {
  AdditionalInfoModalType,
} from "src/components/allFixedFeeConfigsPage/components/additional-info-popover";
import { RegionAwareConfig } from "src/config/facd-all-regions";
import { RegionalDomain } from "src/config/BartLambdaConfig";
import { PartnerPortalLink } from "../fixedFeeConfigPage/partner-portal-link";

// Pagination labels
const fixedFeeConfigPaginationLabels = {
  nextPageLabel: "Next page",
  pageLabel: (pageNumber: number) => `Go to page ${pageNumber}`,
  previousPageLabel: "Previous page",
};

// Function to get matches count text
function getMatchesCountText(count: number) {
  return count === 1 ? "1 match" : `${count} matches`;
}

// Empty state component
function EmptyState({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action: React.ReactNode;
}) {
  return (
    <Box textAlign="center" color="inherit">
      <Box variant="strong" textAlign="center" color="inherit">
        {title}
      </Box>
      <Box variant="p" padding={{ bottom: "s" }} color="inherit">
        {subtitle}
      </Box>
      {action}
    </Box>
  );
}

interface FixedFeeConfigTableProps {
  searchResults: RegionAwareConfig[];
  clients?: Record<string, FuseAccountingCentralDatastore>;
  onRefresh?: () => void;
}

const FixedFeeConfigTableDisplay: React.FC<FixedFeeConfigTableProps> = ({
  searchResults,
  clients,
  onRefresh,
}) => {
  const [allItems, setAllItems] = useState<RegionAwareConfig[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingConfig, setLoadingConfig] = useState<boolean>(false);
  const [configs, setConfigs] = useState<{ [key: string]: FixedFeeConfig }>({});
  const [selectedItems, setSelectedItems] = useState<RegionAwareConfig[]>([]);

  // By default, PRODUCTION ENABLED FixedFeeConfigs which are "currently" ongoing
  const [query, setQuery] = useState<PropertyFilterProps.Query>({
    tokens: [
      { propertyKey: "environment", operator: "=", value: "PRODUCTION" },
      { propertyKey: "dealStatus", operator: "=", value: "ENABLED" },
      { propertyKey: "dealDateStatus", operator: "=", value: "CURRENT" },
    ],
    operation: "and",
  });

  // Fuse Offers column - renders offer IDs as links to Fuse Portal
  const FUSE_OFFERS_COL_DEF: TableProps.ColumnDefinition<RegionAwareConfig> = {
    id: "fuseOffers",
    header: "Fuse Offers",
    cell: (item: RegionAwareConfig) =>
      FUSE_OFFER_LINKS_CELL_WRAPPER(
        item.dealConfig?.fuseOffers,
        item.region as RegionalDomain,
      ),
    minWidth: 300,
    maxWidth: 400,
  };

  // Default preferences for the table
  const FIXED_FEE_CONFIG_DEFAULT_PREFERENCES = {
    pageSize: 15,
    visibleContent: [
      ...FIXED_FEE_CONFIG_ATTRIBUTES.filter(
        (attribute) => !FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[attribute].hidden,
      ),
      FUSE_OFFERS_COL_DEF.id!,
    ],
    wrapLines: true,
    custom: "table",
  };

  // Get unique values for property filter
  const getPropertyFilterOptions = (
    propertyKey: string,
  ): PropertyFilterProps.FilteringOption[] => {
    const uniqueValues = new Set<string>();
    allItems.forEach((item) => {
      const { stringCell, badgeCell, linkCell, numberCell, arrayCell } =
        FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[propertyKey];
      let itemValues: string[] | undefined;
      if (stringCell) {
        itemValues = [STRING_OR_DATE_CELL_WRAPPER(stringCell(item))];
      } else if (badgeCell) {
        const badgeCellResult = badgeCell(item);
        itemValues = [
          badgeCellResult?.uniqueValue ?? badgeCellResult?.content ?? "",
        ];
      } else if (linkCell) {
        itemValues = [LINK_CELL_WRAPPER(linkCell(item, item.region))];
      } else if (numberCell) {
        itemValues = [
          NUMBER_CELL_WRAPPER(numberCell.value(item), numberCell.decimalPlaces),
        ];
      } else if (arrayCell) {
        itemValues = arrayCell(item);
      }

      itemValues?.forEach((itemValue: string) => {
        if (itemValue) {
          uniqueValues.add(itemValue);
        }
      });
    });
    return Array.from(uniqueValues)
      .sort()
      .map((value) => ({ propertyKey, value }));
  };

  useEffect(() => {
    // Focus is now handled by the Input component
  }, []);

  useEffect(() => {
    if (Array.isArray(searchResults)) {
      setAllItems(searchResults);
      setLoading(false);
    } else {
      setAllItems([]);
      setLoading(searchResults === "init");
    }
  }, [searchResults]);

  const getFixedFeeConfig = async (fixedFeeId: string, region?: string) => {
    if (!clients || !region) return;
    setLoadingConfig(true);
    try {
      const response = await clients[region as RegionalDomain].send(
        new GetFixedFeeConfigCommand({ fixedFeeId }),
      );
      setConfigs((prev) => ({
        ...prev,
        [fixedFeeId]: response.fixedFeeConfig!,
      }));
    } catch (error) {
      console.error("An error occurred in getFixedFeeConfig:", error);
    } finally {
      setLoadingConfig(false);
    }
  };

  // Property filter matching function
  const matchesPropertyFilter = (
    item: RegionAwareConfig,
    tokens: readonly PropertyFilterProps.Token[],
  ): boolean => {
    return tokens.every((token) => {
      const { propertyKey, operator, value } = token;
      if (!propertyKey) {
        return false;
      }

      const {
        stringCell,
        dateCell,
        badgeCell,
        linkCell,
        numberCell,
        arrayCell,
      } = FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[propertyKey];
      let stringValue: string | undefined;
      if (stringCell) {
        stringValue = STRING_OR_DATE_CELL_WRAPPER(stringCell(item));
      } else if (dateCell) {
        stringValue = STRING_OR_DATE_CELL_WRAPPER(dateCell(item));
      } else if (badgeCell) {
        const badgeCellResult = badgeCell(item);
        stringValue =
          badgeCellResult?.uniqueValue ?? badgeCellResult?.content ?? "";
      } else if (linkCell) {
        stringValue = LINK_CELL_WRAPPER(linkCell(item, item.region));
      } else if (numberCell) {
        const numericValue: NumericValue | undefined = numberCell.value(item);
        if (!numericValue) {
          return false;
        }

        const numberValue = Number(numericValue);
        const targetNumberValue = Number(value.replace(/,/g, ""));

        switch (operator) {
          case "<":
            return numberValue < targetNumberValue;
          case "<=":
            return numberValue <= targetNumberValue;
          case ">":
            return numberValue > targetNumberValue;
          case ">=":
            return numberValue >= targetNumberValue;
        }
        stringValue = NUMBER_CELL_WRAPPER(
          numericValue,
          numberCell.decimalPlaces,
        );
      }

      if (stringValue !== undefined) {
        switch (operator) {
          case "=":
            return stringValue === value;
          case "!=":
            return stringValue !== value;
          case "<":
            return stringValue < value;
          case "<=":
            return stringValue <= value;
          case ">":
            return stringValue > value;
          case ">=":
            return stringValue >= value;
          case ":":
            return stringValue.includes(value);
          case "!:":
            return !stringValue.includes(value);
        }
        return false;
      }

      // other cases are not string-matchable (date range, array)
      if (arrayCell) {
        const array = arrayCell(item);
        if (array) {
          const found = array.includes(value);
          if (operator === ":") return !!found;
          if (operator === "!:") return !found;
        }
      }

      return false;
    });
  };

  const clearFilter = () => {
    setQuery({ tokens: [], operation: "and" });
    actions.setFiltering("");
  };

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(allItems, {
    filtering: {
      empty: (
        <EmptyState
          title="No FixedFee Configs"
          subtitle="No FixedFee Configs to display."
          action={null}
        />
      ),
      noMatch: (
        <EmptyState
          title="No matches"
          subtitle="We can't find a match."
          action={<Button onClick={clearFilter}>Clear all filters</Button>}
        />
      ),
      filteringFunction: (item, filteringText) => {
        // Handle property filter tokens
        if (
          query.tokens.length > 0 &&
          !matchesPropertyFilter(item, query.tokens)
        ) {
          return false;
        }

        // Handle free text search
        if (filteringText) {
          const filteringTextLowerCase = filteringText.toLowerCase();
          return FIXED_FEE_CONFIG_ATTRIBUTES.some((field) => {
            const {
              stringCell,
              dateCell,
              badgeCell,
              linkCell,
              numberCell,
              arrayCell,
            } = FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[field];
            let itemValue;
            if (stringCell) {
              itemValue = STRING_OR_DATE_CELL_WRAPPER(stringCell(item));
            } else if (dateCell) {
              itemValue = STRING_OR_DATE_CELL_WRAPPER(dateCell(item));
            } else if (badgeCell) {
              itemValue = badgeCell(item)?.content ?? "";
            } else if (linkCell) {
              itemValue = LINK_CELL_WRAPPER(linkCell(item, item.region));
            } else if (numberCell) {
              itemValue = NUMBER_CELL_WRAPPER(
                numberCell.value(item),
                numberCell.decimalPlaces,
              );
            } else if (arrayCell) {
              itemValue = arrayCell(item)?.join(", ");
            }
            // date range not supported, default to false
            return (
              itemValue?.toLowerCase()?.includes(filteringTextLowerCase) ??
              false
            );
          });
        }

        return true;
      },
    },
    pagination: {
      pageSize: FIXED_FEE_CONFIG_DEFAULT_PREFERENCES.pageSize,
    },
    sorting: {},
    selection: {},
  });

  return (
    <Box padding={{ left: "l", right: "l" }}>
      <Table
        {...collectionProps}
        selectionType="single"
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) =>
          setSelectedItems(detail.selectedItems)
        }
        visibleColumns={FIXED_FEE_CONFIG_DEFAULT_PREFERENCES.visibleContent}
        wrapLines={FIXED_FEE_CONFIG_DEFAULT_PREFERENCES.wrapLines}
        loading={loading}
        loadingText="Loading FixedFeeConfigs"
        stickyHeaderVerticalOffset={1}
        stickyColumns={{ first: 1, last: 0 }}
        variant="full-page"
        header={
          <Header
            variant="awsui-h1-sticky"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="normal"
                  iconName="refresh"
                  onClick={() => {
                    setSelectedItems([]);
                    onRefresh?.();
                  }}
                  disabled={loading}
                />
                {[
                  AdditionalInfoModalType.Issuance,
                  AdditionalInfoModalType.AdjustmentSubBalanceShift,
                  AdditionalInfoModalType.Matcher,
                ].map((modalType) => {
                  const isTac =
                    selectedItems[0]?.fixedFeeType ===
                    FixedFeeType.TERMINATION_AT_CONVENIENCE;
                  const isTacDisabled =
                    isTac &&
                    modalType ===
                      AdditionalInfoModalType.AdjustmentSubBalanceShift;
                  const isDisabled =
                    selectedItems.length === 0 || isTacDisabled;

                  const button = (
                    <Button
                      disabled={isDisabled}
                      onClick={() => {
                        if (
                          selectedItems[0] &&
                          !(selectedItems[0].fixedFeeId! in configs)
                        ) {
                          getFixedFeeConfig(
                            selectedItems[0].fixedFeeId!,
                            selectedItems[0].region,
                          );
                        }
                      }}
                      variant="normal"
                    >
                      {modalType === AdditionalInfoModalType.Issuance
                        ? "View Issuance"
                        : modalType ===
                            AdditionalInfoModalType.AdjustmentSubBalanceShift
                          ? "View Adjustment/SubBalanceShift"
                          : "View Matcher"}
                    </Button>
                  );

                  if (isTacDisabled) {
                    return (
                      <span
                        key={modalType}
                        title="Not supported for config with Termination at Convenience"
                      >
                        {button}
                      </span>
                    );
                  }

                  return (
                    <AdditionalInfoPopover
                      key={modalType}
                      fixedFeeConfig={
                        selectedItems[0]
                          ? configs[selectedItems[0].fixedFeeId!]
                          : undefined
                      }
                      loading={loadingConfig}
                      child={button}
                      domain={
                        (selectedItems[0]?.region as RegionalDomain) ??
                        "devo-FE"
                      }
                      modalType={modalType}
                    />
                  );
                })}
                <PartnerPortalLink fixedFeeConfig={selectedItems[0]} />
              </SpaceBetween>
            }
            counter={`(${allItems.length})`}
          >
            FixedFee Configs
          </Header>
        }
        columnDefinitions={[
          ...getFixedFeeConfigTableColDefs(),
          FUSE_OFFERS_COL_DEF,
        ]}
        items={items}
        pagination={
          <Pagination
            {...paginationProps}
            ariaLabels={fixedFeeConfigPaginationLabels}
          />
        }
        filter={
          <PropertyFilter
            {...filterProps}
            query={query}
            onChange={({ detail }) => setQuery(detail)}
            filteringProperties={FIXED_FEE_CONFIG_FILTERS}
            filteringOptions={FIXED_FEE_CONFIG_FILTERS.reduce<
              PropertyFilterProps.FilteringOption[]
            >(
              (acc, property) =>
                acc.concat(getPropertyFilterOptions(property.key)),
              [],
            )}
            countText={getMatchesCountText(filteredItemsCount ?? 0)}
            expandToViewport={true}
            filteringAriaLabel="Find FixedFee Configs"
            filteringPlaceholder="Find FixedFee Configs"
            i18nStrings={{
              filteringPlaceholder: "Find APIs",
              operationAndText: "and",
              operationOrText: "or",
              clearFiltersText: "Clear filters",
              operatorContainsText: "Contains",
              operatorDoesNotContainText: "Does not contain",
              operatorEqualsText: "Equals",
              operatorDoesNotEqualText: "Does not equal",
              applyActionText: "Apply",
            }}
          />
        }
      />
    </Box>
  );
};

export default FixedFeeConfigTableDisplay;
